import React, { useCallback, useState, useEffect, useLayoutEffect } from 'react';
import { View, ActivityIndicator, Platform, StyleSheet, SafeAreaView } from 'react-native';

import { useTranslation } from 'react-i18next';
import { ActionSheetDropdown } from '../../ActionSheetDropdown';
import { LocaleConfig } from 'react-native-calendars';
import { HeaderBackButton } from '@react-navigation/elements';
import { Color, CommonStyles, CommonProperties } from '../../../styles';
import moment from 'moment';
import { useComponentTKey, i18nNS } from '../../../i18n';
import { useDispatch } from 'react-redux';
import { openAlert } from '../../../store/actions/alert';
import RowView from '../../RowView';
import BottomButton from '../../BottomButton';
import useCalendarLocaleConfig from '../../../utils/useCalendarLocaleConfig';
import { AACalendar } from './AACalendar';
import BaseText from '../../BaseText';
import { getRedeemCalendar } from '../../../store/actions/search';
import { Screens } from '../../../constants/ScreenNames';
import { scale } from 'react-native-size-matters';
import { isRoundTrip } from '../../../utils/search.utils';
import { getPoints } from '../../../utils/currency.utils';

const AACalendarModal = (props) => {
  const { navigation, route } = props;
  LocaleConfig.locales['en'] = useCalendarLocaleConfig();
  LocaleConfig.defaultLocale = 'en';
  const { t } = useTranslation(i18nNS.searchWidget);
  const { tKey } = useComponentTKey('AACalendarModal');
  const dispatch = useDispatch();
  const { returnScreen, fromDate, toDate, authNumber, airRequestType } = route.params || {};
  const [transitioned, setTransitioned] = useState(false);

  const [selectedOption, setSelectedOption] = useState('');
  const [redeemCalendar, setRedeemCalendar] = useState(null);
  const [options, setOptions] = useState([{ text: '', underText: '' }]);
  const [selectedAwardCode, setSelectedAwardCode] = useState('');
  const [departDate, setDepartDate] = useState(fromDate);
  const [returnDate, setReturnDate] = useState(toDate);

  useLayoutEffect(() => {
    navigation.setOptions({
      headerLeft: (headerLeftProps) => (
        <HeaderBackButton
          {...headerLeftProps}
          onPress={() => {
            navigation.navigate(Screens.AASearchScreen);
          }}
        />
      ),
    });
  }, [navigation]);

  const findDayAward = useCallback(
    (date, leg) => {
      const weeks = redeemCalendar.RedeemCalendar[leg].Week;
      let dayAward = null;
      weeks.forEach((week) => {
        if (dayAward) {
          return;
        }
        const day = week.Day.find((day) => day.DisplayDate === date.dateString);
        try {
          dayAward = day?.AwardTypes?.find(
            (award) => award.AwardCode === selectedAwardCode.awardCode
          );
        } catch (error) {
          __DEV__ && console.error(error);
        }
      });
      return dayAward;
    },
    [selectedAwardCode, redeemCalendar]
  );

  useEffect(() => {
    (async () => {
      setRedeemCalendar(await dispatch(getRedeemCalendar(authNumber)));
    })();
  }, [authNumber, dispatch]);

  useEffect(() => {
    if (redeemCalendar) {
      if (redeemCalendar.RedeemCalendarRule === null || redeemCalendar.RedeemCalendar === null) {
        dispatch(openAlert({ message: 'Something went wrong' }));
      }
      const aheadOptions = redeemCalendar.RedeemCalendarRule.PlanAhead.map((option) => {
        return {
          text: `PlanAhead ${getPoints(option.Points)}`,
          underText: `${option.AwardType} ${getPoints(option.Points)}`,
          points: option.Points,
          awardCode: option.AwardCode,
        };
      });
      const anytimeOptions = redeemCalendar.RedeemCalendarRule.Anytime.map((option) => {
        return {
          text: `Anytime ${getPoints(option.Points)}`,
          underText: `${option.AwardType} ${getPoints(option.Points)}`,
          points: option.Points,
          awardCode: option.AwardCode,
        };
      });
      setOptions(aheadOptions.concat(anytimeOptions));
      setSelectedAwardCode(aheadOptions.concat(anytimeOptions)[0]);
      setTransitioned(true);
    }
  }, [redeemCalendar, dispatch]);

  const onApplyHandler = useCallback(
    (e) => {
      // need to stop this from bubbling because it was activating the react navigation bottom bar and switching tabs
      e.preventDefault();
      if (!findDayAward({ dateString: departDate }, 0)) {
        dispatch(openAlert({ message: t(tKey('UnavailableFirstDate')) }));
        return;
      }

      if (isRoundTrip(airRequestType)) {
        if (!findDayAward({ dateString: returnDate }, 1)) {
          dispatch(openAlert({ message: t(tKey('UnavailableSecondDate')) }));
          return;
        }

        if (new Date(departDate).getTime() > new Date(returnDate).getTime()) {
          dispatch(openAlert({ message: t(tKey('ChronologicalOrder')) }));
          return;
        }
      }

      navigation.navigate(returnScreen, {
        selectedDates: {
          departDate: departDate,
          arrivalDate: returnDate,
        },
        selectedAwardCode,
        fetchBX: true,
      });
    },
    [
      t,
      tKey,
      dispatch,
      navigation,
      returnScreen,
      findDayAward,
      departDate,
      returnDate,
      selectedAwardCode,
    ]
  );

  const HeadBand = (props) => {
    return (
      <View style={styles.headBandContainer}>
        <View style={styles.headBand}>
          <BaseText
            style={[
              CommonStyles.h2,
              CommonProperties.my2,
              CommonProperties.mx5,
              styles.headBandText,
            ]}
          >
            {props.children}
          </BaseText>
        </View>
      </View>
    );
  };

  useEffect(() => {
    if (redeemCalendar) {
      setTransitioned(true);
    }
  }, [departDate, returnDate, redeemCalendar]);

  return (
    <SafeAreaView style={styles.pageContainer}>
      {!transitioned ? <ActivityIndicator></ActivityIndicator> : <></>}
      {transitioned ? (
        <View style={[CommonStyles.flex1, {}]}>
          <RowView style={[CommonStyles.m2, CommonStyles.mt0]}>
            <ActionSheetDropdown
              selectedOption={selectedOption}
              onDropdownPress={() => {
                //need to set the transition to false to force the calendar update
                setTransitioned(false);
              }}
              onChangedOption={(selected) => {
                setSelectedOption(selected);
                setSelectedAwardCode(options.find((option) => option.text === selected));
                setTransitioned(true);
              }}
              options={options}
              containerStyle={styles.pageContainer}
            />
          </RowView>
          <View style={[styles.calendarParentContainer, styles.shadowCalendar]}>
            <HeadBand>{`Depart - ${moment(departDate).format('LL')}`}</HeadBand>
            <AACalendar
              redeemCalendar={redeemCalendar}
              findDayAward={findDayAward}
              leg={0}
              date={departDate}
              onDaySelected={(date) => {
                setDepartDate((current) => {
                  //need to set the transition to false to force the calendar update when month changed
                  if (new Date(current).getMonth() !== new Date(date.dateString).getMonth()) {
                    setTransitioned(false);
                  }
                  return date.dateString;
                });
              }}
            />
          </View>
          {isRoundTrip(airRequestType) ? (
            <View style={[styles.calendarParentContainer, styles.shadowCalendar]}>
              <HeadBand>{`Return - ${moment(returnDate).format('LL')}`}</HeadBand>
              <AACalendar
                redeemCalendar={redeemCalendar}
                findDayAward={findDayAward}
                leg={1}
                date={returnDate}
                onDaySelected={(date) => {
                  setReturnDate((current) => {
                    //need to set the transition to false to force the calendar update when month changed
                    if (new Date(current).getMonth() !== new Date(date.dateString).getMonth()) {
                      setTransitioned(false);
                    }
                    return date.dateString;
                  });
                }}
              />
            </View>
          ) : (
            <></>
          )}

          <BottomButton label={t('common:Apply')} onPress={onApplyHandler}></BottomButton>
        </View>
      ) : (
        <></>
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  pageContainer: CommonProperties.defaultContainer,
  // not sure why overflowY is required on web in order to allow calendar to scroll
  calendarParentContainer:
    Platform.OS === 'web'
      ? { flex: 1, overflowY: 'scroll', marginHorizontal: 4 }
      : { flex: 1, marginHorizontal: 4 },
  shadowCalendar: {
    shadowColor: Color.shadow,
    shadowOpacity: 1,
    shadowRadius: 3.84,
    elevation: 5,
  },
  headBandContainer: {
    backgroundColor: Color.primary,
    ...CommonProperties.borderTopRadius,
    ...CommonProperties.shadow,
    marginBottom: 1,
    height: scale(32),
  },
  headBand: { position: 'absolute', left: 0, right: 0, top: 0 },
  headBandText: { color: Color.accent, alignSelf: 'center' },
});

export default AACalendarModal;
